// ============================================================================
// ACCESS RESOLVER — host (own/shared units) wins over cleaner-only.
// Dual-role accounts resolve as owner here; ViewContext chooses Properties vs Jobs.
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type AccessType =
  | { type: "single_task"; taskId: string; propertyId: string }
  | { type: "multiple_tasks"; taskIds: string[] }
  | { type: "full_dashboard"; role: "owner" | "cleaner"; workspaceLock?: "properties" | "jobs" }
  | { type: "invite_invalid"; reason?: string }
  | { type: "unauthorized" }
  | { type: "no_access" };

export const useAccessResolver = () => {
  const { user, profile } = useAuth();

  return useQuery<AccessType>({
    queryKey: ["access-resolver", user?.id, profile?.id, profile?.role],
    queryFn: async () => {
      if (!user || !profile) return { type: "unauthorized" as const };

      const [{ data: cleanerRow }, ownedRes, cohostRes] = await Promise.all([
        supabase.from("cleaners").select("id").eq("user_id", user.id).maybeSingle(),
        supabase
          .from("properties")
          .select("id", { count: "exact", head: true })
          .eq("owner_id", user.id)
          .is("archived_at", null)
          .is("deleted_at", null)
          .limit(1),
        (supabase as unknown as { from: (table: string) => ReturnType<typeof supabase.from> })
          .from("property_cohosts")
          .select("id", { count: "exact", head: true })
          .eq("cohost_user_id", user.id)
          .not("accepted_at", "is", null)
          .limit(1),
      ]);

      const ownsProperty = (ownedRes.count ?? 0) > 0 || profile.role === "owner";
      const isCohost = (cohostRes.count ?? 0) > 0;
      const isCleaner = Boolean(cleanerRow?.id) || profile.role === "cleaner";
      let hasCleanerAssignment = false;
      if (cleanerRow?.id) {
        const assignRes = await supabase
          .from("cleaner_assignments")
          .select("id", { count: "exact", head: true })
          .eq("cleaner_id", cleanerRow.id)
          .limit(1);
        hasCleanerAssignment = (assignRes.count ?? 0) > 0;
      }

      // Owner, or Team on one unit and still assigned to clean another: both workspaces.
      // Team on every unit they clean stays on Properties.
      if (ownsProperty || (isCohost && hasCleanerAssignment)) {
        return { type: "full_dashboard", role: "owner" as const };
      }
      if (isCohost) {
        return { type: "full_dashboard", role: "owner" as const, workspaceLock: "properties" as const };
      }
      if (isCleaner) {
        return { type: "full_dashboard", role: "cleaner" as const, workspaceLock: "jobs" as const };
      }

      return { type: "full_dashboard", role: "owner" as const };
    },
    enabled: !!user && !!profile,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });
};

export const useInviteResolver = (inviteToken: string | null) => {
  return useQuery({
    queryKey: ["invite-resolver", inviteToken],
    queryFn: async () => {
      if (!inviteToken) return null;
      const { data, error } = await supabase
        .from("actor_invites")
        .select("invite_id, task_id, property_id, email, role_hint, expires_at, accepted_at")
        .eq("token", inviteToken)
        .maybeSingle();
      if (error || !data) return null;
      return {
        inviteId: data.invite_id,
        taskId: data.task_id,
        propertyId: data.property_id,
        email: data.email,
        roleHint: data.role_hint,
        expiresAt: data.expires_at,
        acceptedAt: data.accepted_at,
      };
    },
    enabled: !!inviteToken,
    staleTime: 1000 * 60 * 5,
  });
};

export const acceptInvite = async (inviteToken: string, _userId: string) => {
  const { error } = await supabase
    .from("actor_invites")
    .update({ accepted_at: new Date().toISOString() })
    .eq("token", inviteToken);
  if (error) {
    console.error("[acceptInvite] Error accepting invite:", error);
    return { error };
  }
  return { error: null };
};
